import React, { memo } from "react";
import { UserChatMessage, UserChatMessageRef } from "../../../components/UserChatMessage";
import { AnswerLoading, AnswerError, UnifiedAnswer } from "../../../components/Answer";
import { AnswerRegenarate } from "../../../components/Answer/AnswerRegenarate";
import { AnalysisPanelTabs } from "../../../components/AnalysisPanel";
import { AppType, ChatAppResponse, Feedback, ChatSessionMode } from "../../../api/models";
import { Histories } from "../../../api";
import { ChatMessageList, ChatMessageListProps } from "./ChatMessageList";
import styles from "../Chat.module.css";

interface LegacyChatMessagesProps {
    answers: any[];
    isLoading: boolean;
    isStreaming: boolean;
    isTabClick: React.MutableRefObject<string>;
    error: unknown;
    selectedAppType: AppType;
    selectedAnswer: number;
    activeAnalysisPanelTab: AnalysisPanelTabs | undefined;
    doctype: boolean;
    useSuggestFollowupQuestions: boolean;
    isAdmin: boolean;
    IsOnEdit: boolean;
    selectedQuestion: number;
    applyRestriction: boolean;
    searchTerm: string;
    versionIndexMap: Record<string, number>;
    histories: Histories;
    lastQuestionRef: any;
    currentTargetQuestionId: React.MutableRefObject<number>;
    onShowCitation: (citation: string, citationIndex: number, index: number) => void;
    onToggleTab: (tab: AnalysisPanelTabs, index: number) => void;
    onFollowupQuestionClicked: (question: string, appType: AppType) => void;
    onFeedbackSubmitClicked: (feedback: Feedback, conv_id: string, appTypeValue: AppType) => void;
    onEditClick: (checked: boolean, index: number, questionInfo: any) => void;
    onSendQuestion: (question: string, appType: AppType, answer: any, index: number, questionDetails: any) => void;
    onRetry: (index: number, answer: any, questionDetails: any) => void;
    onVersionChange: (versionIndex: number, parentMessageId: string, answerIndex: number) => void;
    setSelectedAnswer: (index: number) => void;
    setSelectedCitation: (index: number) => void;
    setSelectedAppType: (appType: AppType) => void;
    answerAreaRef: React.RefObject<HTMLDivElement>;
    setRefsForAnswersRender: (node: HTMLDivElement | null, index: number) => void;
    setRefsForStreamedAnswersRender: (node: HTMLDivElement | null, index: number) => void;
    lastQuestionIndexValue: React.MutableRefObject<number>;
    activateAnswerTour: boolean;
    setActivateAnswerTour: (value: boolean) => void;
    currentView: ChatSessionMode;
    appInfo: any;
    isWapUser: boolean;
    chatMessageStreamEnd: React.RefObject<HTMLDivElement | null>;
    userChatMessageRefs: React.MutableRefObject<{ [key: number]: UserChatMessageRef | null }>;
}

export type ChatMessagesProps = LegacyChatMessagesProps | ChatMessageListProps;

const isChatMessageListProps = (props: ChatMessagesProps): props is ChatMessageListProps => {
    return "renderUnifiedAnswer" in props && "setOverAllCurrentSessionDataPoints" in props && "setAnswers" in props;
};

const ChatMessagesAreaComponent: React.FC<ChatMessagesProps> = props => {
    if (isChatMessageListProps(props)) {
        return <ChatMessageList {...props} />;
    }

    const {
        answers,
        isLoading,
        isStreaming,
        isTabClick,
        error,
        selectedAppType,
        selectedAnswer,
        activeAnalysisPanelTab,
        doctype,
        useSuggestFollowupQuestions,
        isAdmin,
        IsOnEdit,
        selectedQuestion,
        applyRestriction,
        searchTerm,
        versionIndexMap,
        histories,
        lastQuestionRef,
        currentTargetQuestionId,
        onShowCitation,
        onToggleTab,
        onFollowupQuestionClicked,
        onFeedbackSubmitClicked,
        onEditClick,
        onSendQuestion,
        onRetry,
        onVersionChange,
        setSelectedAnswer,
        setSelectedCitation,
        setSelectedAppType,
        answerAreaRef,
        setRefsForAnswersRender,
        setRefsForStreamedAnswersRender,
        lastQuestionIndexValue,
        activateAnswerTour,
        setActivateAnswerTour,
        currentView,
        appInfo,
        isWapUser,
        chatMessageStreamEnd,
        userChatMessageRefs
    } = props;
    const renderUnifiedAnswer = (answer: any, questionDetails: any, index: number, isStreaming: boolean, isHistory: boolean = false) => {
        const response = answer[1];
        const questionDetail = answer[0] || questionDetails;

        return (
            <UnifiedAnswer
                isStreaming={isStreaming}
                key={index}
                doctype={doctype}
                response={response}
                conversationDate={questionDetail?.conversation_date}
                isSelected={isHistory ? selectedAnswer === index && activeAnalysisPanelTab !== undefined : false}
                onCitationClicked={(c: string, citationIndex: number) => onShowCitation(c, citationIndex, index)}
                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                onFollowupQuestionClicked={(q: string, appType: AppType) => onFollowupQuestionClicked(q, appType)}
                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                onFeedbackSubmitClicked={(feedback: any, conv_id: string, appTypeValue: AppType) => onFeedbackSubmitClicked(feedback, conv_id, appTypeValue)}
                updateStreamedAnswer={(index: number, updatedAnswer: ChatAppResponse, appType: AppType) => {
                    // Handle streamed answer updates
                }}
                primaryId={index?.toString()}
                onClickLikeDislike={() => {}}
                isAdminUser={isAdmin}
                displayLink={() => {}}
                isLastAnswer={answers?.length === 0 || (answers?.length > 0 && answers?.length - 1 === index)}
                isFeedbackPage={false}
                onSend={(appType: AppType) => onSendQuestion("", appType, response, index, questionDetail)}
                isLoading={isLoading}
                selectedAppType={selectedAppType}
                setSelectedAppType={setSelectedAppType}
                lastAnswerIndexRefValue={currentTargetQuestionId.current}
                onTabClicked={() => {
                    setSelectedAnswer(index);
                    setSelectedCitation(0);
                    currentTargetQuestionId.current = index;
                    sessionStorage.setItem("isTabClicked", "true");
                }}
                currentPageView={currentView}
                answerAreaRef={answerAreaRef}
                chatResponseAreaRef={null}
                lastQuestionIndexValue={(lastQuestionIndexValue.current === 0 ? 0 : lastQuestionIndexValue.current - 1)?.toString()}
                activateAnswerTour={activateAnswerTour}
                setActivateAnswerTour={setActivateAnswerTour}
                combinedAppTypeResponse={lastQuestionRef.current?.combinedAppTypeResponse}
                isHistory={isHistory}
                currentAnswerIndex={0}
                isRegenerateActive={false}
                onAnswerVersionChange={() => {}}
                answerVersions={0}
            />
        );
    };

    return (
        <div ref={answerAreaRef} className={styles.chatMessageStream}>
            {Array.isArray(answers) &&
                answers.map((answer, index) => (
                    <div key={index}>
                        <UserChatMessage
                            ref={el => {
                                userChatMessageRefs.current[index] = el;
                            }}
                            key={index}
                            message={answer[0].extractedQuestion}
                            onSend={(question, appType: string) => {
                                onSendQuestion(question, currentTargetQuestionId.current === 0 ? "ALL" : selectedAppType, answer[1], index, answer[0]);
                            }}
                            index={index}
                            answers={answers}
                            onEditClick={(value: any, questionInfo: any) => {
                                onEditClick(value, index, questionInfo);
                            }}
                            IsOnEdit={IsOnEdit}
                            isAdminUser={isAdmin}
                            selectedQuestion={selectedQuestion === index}
                            applyRestriction={applyRestriction}
                            reference={null}
                            responseGenerationActive={isLoading || isStreaming}
                            selectedAppType={selectedAppType}
                            templateId={answer[0].templateId}
                            questionContent={answer[0].question}
                            sourcePage={"chat"}
                            fileAttachments={answer[0].filesAttached}
                            searchTerm={searchTerm}
                            setSelectedAppType={setSelectedAppType}
                            currentVersionIndex={
                                versionIndexMap[
                                    (answer[0]?.parentMessageId && answer[0].parentMessageId !== "0" ? answer[0].parentMessageId : answer[0].question_id) ?? ""
                                ] || 0
                            }
                            onVersionChange={(versionIndex: number, parentMessageId: string, answerIndex: number) => {
                                onVersionChange(versionIndex, parentMessageId, answerIndex);
                            }}
                            histories={histories}
                        />
                        <div className={styles.chatMessageGpt} ref={element => setRefsForAnswersRender(element, index)}>
                            {(answer[1][selectedAppType] === undefined || answer[1][selectedAppType] === null) &&
                            !isLoading &&
                            !isStreaming &&
                            Object.keys(answer[1]).length > 1 ? (
                                <div className={styles.chatMessageGptMinWidth}>
                                    <AnswerRegenarate
                                        selectedAppType={selectedAppType}
                                        isLoading={isLoading}
                                        isStreaming={isStreaming}
                                        answer={answer[1]}
                                        conversationDate={answer[0]?.conversation_date}
                                        onRetry={() => onRetry(index, answer[1], answer[0])}
                                        setSelectedAppType={setSelectedAppType}
                                        onTabClicked={() => {
                                            currentTargetQuestionId.current = index;
                                            setSelectedAnswer(index);
                                            setSelectedCitation(0);
                                            sessionStorage.setItem("isTabClicked", "true");
                                        }}
                                        doctype={doctype}
                                        primaryId={index.toString()}
                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                    />
                                </div>
                            ) : (answer[1][selectedAppType]?.context.keywords !== undefined && answer[1][selectedAppType]?.context.feedbackgiven === false) ||
                              answer[1][selectedAppType]?.context.feedback?.category === 0 ||
                              (!answer[1][selectedAppType] &&
                                  answer[1]["ALL"]?.context.keywords !== undefined &&
                                  answer[1]["ALL"]?.context.feedbackgiven === false) ||
                              answer[1]["ALL"]?.context.feedback?.category === 0 ? (
                                renderUnifiedAnswer(answer, answer[0], index, isStreaming, false)
                            ) : (
                                renderUnifiedAnswer(answer, answer[0], index, isStreaming, true)
                            )}
                        </div>
                    </div>
                ))}

            {isLoading && isTabClick.current === "false" && (
                <>
                    <UserChatMessage
                        message={lastQuestionRef.current.extractedQuestion}
                        onEditClick={(value: any) => onEditClick(value, 0, null)}
                        responseGenerationActive={isLoading || isStreaming}
                        selectedAppType={selectedAppType}
                        templateId={lastQuestionRef.current.templateId}
                        questionContent={lastQuestionRef.current.question}
                        sourcePage={"chat"}
                        isAdminUser={isAdmin}
                        searchTerm={""}
                        fileAttachments={lastQuestionRef.current.filesAttached}
                        setSelectedAppType={setSelectedAppType}
                        currentVersionIndex={0}
                    />
                    <div className={styles.chatMessageGptMinWidth}>
                        <AnswerLoading selectedAppType={selectedAppType} questionFilesAttached={lastQuestionRef.current.filesAttached} />
                    </div>
                </>
            )}

            {error
                ? isTabClick.current === "false" && (
                      <>
                          <UserChatMessage
                              message={lastQuestionRef.current.extractedQuestion}
                              onEditClick={(value: any) => onEditClick(value, 0, null)}
                              responseGenerationActive={isLoading || isStreaming}
                              selectedAppType={selectedAppType}
                              templateId={lastQuestionRef.current.templateId}
                              questionContent={lastQuestionRef.current.question}
                              sourcePage={"chat"}
                              isAdminUser={isAdmin}
                              searchTerm={""}
                              fileAttachments={lastQuestionRef.current.filesAttached}
                              setSelectedAppType={setSelectedAppType}
                              currentVersionIndex={0}
                          />
                          <div className={styles.chatMessageGptMinWidth}>
                              <AnswerError
                                  error={error?.toString()}
                                  onRetry={() => {
                                      onRetry(-1, null, lastQuestionRef.current);
                                  }}
                              />
                          </div>
                      </>
                  )
                : null}

            <div ref={chatMessageStreamEnd} />
        </div>
    );
};

export const ChatMessagesArea = memo(ChatMessagesAreaComponent);
